import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import {
    FaChevronLeft,
    FaChevronRight,
    FaSearch,
    FaComments,
} from "react-icons/fa";
import { useDebounce } from "../../util/hooks/useDebounce";
import { fetchMessages } from "../../features/message/Messageservice";
import ECSLoader from "../../components/common/EcsLoader";
import ChatModal from "./ChatModal";
import { whtsappBg } from "../../asset";
import { StatusShow } from "../../components/common/StausShow";

export default function MsgList() {
    const dispatch = useDispatch<AppDispatch>();

    const [messages, setMessages] = useState<any[]>([]);
    const [pageInfo, setPageInfo] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [page, setPage] = useState(0);

    const [openChat, setOpenChat] = useState(false);
    const [selectedPhone, setSelectedPhone] = useState("");

    const [filters, setFilters] = useState({
        toNumber: "",
        templateName: "",
        status: "",
        deliveryStatus: ""
    });

    const debouncedFilters = useDebounce(filters, 500);

    useEffect(() => {
        setPage(0);
    }, [debouncedFilters]);

    const loadMessages = async () => {
        setLoading(true);
        setError("");

        try {
            const result = await dispatch(
                fetchMessages({ page, filters: debouncedFilters })
            ).unwrap();

            setMessages(result.results);
            setPageInfo(result.page);
        } catch (err: any) {
            setError(err?.message || "Failed to load messages");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadMessages();
    }, [dispatch, page, debouncedFilters]);

    const totalPages = pageInfo.count ?? 0;

    const currentStart =
        pageInfo.index != null ? pageInfo.index * pageInfo.size + 1 : 1;

    const currentEnd = Math.min(
        (pageInfo.index + 1) * pageInfo.size,
        pageInfo.total || 0
    );

    const formatDate = (epoch: number) => {
        if (!epoch) return "-";

        return new Date(epoch).toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    return (
        <>
            <div className="w-full bg-white rounded-3xl p-4 sm:p-6 relative">
                <ECSLoader show={loading} />

                {error && (
                    <div className="text-red-500 mb-3 text-sm font-medium">{error}</div>
                )}

                {/* Header row */}
                <div className="flex justify-between items-center mb-5">
                    <div>
                        <h2 className="font-bold text-gray-800 text-lg">Message Logs</h2>
                        <p className="text-xs text-gray-400 mt-0.5">
                            All outgoing WhatsApp messages
                        </p>
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center gap-2 text-sm">
                        <span className="text-gray-500 hidden sm:inline">
                            <span className="text-blue-500 font-bold">
                                {currentStart}–{currentEnd}
                            </span>{" "}
                            of {pageInfo.total || 0}
                        </span>
                        <button
                            disabled={page === 0 || loading}
                            onClick={() => setPage((p) => p - 1)}
                            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition"
                        >
                            <FaChevronLeft size={12} />
                        </button>
                        <button
                            disabled={(totalPages > 0 && page + 1 >= totalPages) || loading}
                            onClick={() => setPage((p) => p + 1)}
                            className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition"
                        >
                            <FaChevronRight size={12} />
                        </button>
                    </div>
                </div>

                {/* Table wrapper */}
                <div
                    className={`w-full overflow-x-auto transition-opacity duration-300 ${loading ? "opacity-40 pointer-events-none" : "opacity-100"
                        }`}
                >
                    <table className="w-full text-sm table-fixed">
                        <colgroup>
                            <col className="w-[7%]" />   {/* ID */}
                            <col className="w-[18%]" />  {/* To Number */}
                            <col className="w-[22%]" />  {/* Template */}
                            <col className="w-[12%]" />  {/* Language */}
                            <col className="w-[13%]" />  {/* Status */}
                            <col className="w-[13%]" />  {/* Status */}
                            <col className="w-[22%]" />  {/* Sent At */}
                            <col className="w-[6%]" />   {/* Chat */}
                        </colgroup>

                        <thead>
                            {/* Column headers */}
                            <tr className="border-b border-gray-100">
                                <th className="pb-3 text-left text-xs font-bold uppercase pl-2">
                                    ID
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase">
                                    To Number
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase">
                                    Template
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase">
                                    Language
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase">
                                    Status
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase">
                                    Delivery Status
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase">
                                    Sent At
                                </th>
                                <th className="pb-3 text-left text-xs font-bold uppercase">
                                    Chat
                                </th>
                            </tr>

                            {/* Search / filter row */}
                            <tr className="border-b border-gray-100">
                                <th className="py-3 pl-2" />
                                <th className="py-3 pr-3">
                                    <SearchBox
                                        value={filters.toNumber}
                                        placeholder="Search number..."
                                        onChange={(v) =>
                                            setFilters((p) => ({ ...p, toNumber: v }))
                                        }
                                    />
                                </th>
                                <th className="py-3 pr-3">
                                    <SearchBox
                                        value={filters.templateName}
                                        placeholder="Search template..."
                                        onChange={(v) =>
                                            setFilters((p) => ({ ...p, templateName: v }))
                                        }
                                    />
                                </th>
                                <th className="py-3" />
                                <th className="py-3 pr-3">
                                    <select
                                        value={filters.status}
                                        onChange={(e) =>
                                            setFilters((p) => ({ ...p, status: e.target.value }))
                                        }
                                        className="w-full border border-gray-200 rounded-lg h-9 px-2 text-xs font-medium text-gray-600 outline-none focus:ring-1 focus:ring-blue-400"
                                    >
                                        <option value="">All</option>
                                        <option value="SENT">Sent</option>
                                        <option value="FAILED">Failed</option>
                                    </select>
                                </th>
                                <th className="py-3 pr-3">
                                    <select
                                        value={filters.deliveryStatus}
                                        onChange={(e) =>
                                            setFilters((p) => ({ ...p, deliveryStatus: e.target.value }))
                                        }
                                        className="w-full border border-gray-200 rounded-lg h-9 px-2 text-xs font-medium text-gray-600 outline-none focus:ring-1 focus:ring-blue-400"
                                    >
                                        <option value="">All</option>
                                        <option value="DELIVERED">Delivered</option>
                                        <option value="FAILED">Failed</option>
                                        <option value="READ">Read</option>
                                    </select>
                                </th>
                                <th />
                                <th />
                            </tr>
                        </thead>

                        <tbody className="divide-y divide-gray-50">
                            {messages.length === 0 && !loading ? (
                                <tr>
                                    <td colSpan={7} className="text-center py-16 text-gray-400">
                                        No messages found.
                                    </td>
                                </tr>
                            ) : (
                                messages.map((item, index) => (
                                    <tr key={item.id ?? index} className="hover:bg-gray-50">
                                        <td className="py-4 pl-2 text-gray-400 text-xs font-mono truncate">
                                            {item.id}
                                        </td>

                                        <td className="py-4 pr-3 font-semibold text-gray-700 truncate">
                                            {item.toNumber}
                                        </td>

                                        <td className="py-4 pr-3">
                                            <span className="font-mono text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-md truncate block w-fit max-w-full">
                                                {item.templateName}
                                            </span>
                                        </td>

                                        <td className="py-4 pr-3 text-gray-500 text-xs uppercase truncate">
                                            {item.language}
                                        </td>

                                        <td className="py-4 pr-3">
                                            <StatusShow status={item.status} />
                                        </td>

                                        <td className="py-4 pr-3">
                                            <StatusShow status={item.deliveryStatus} />
                                        </td>

                                        <td className="py-4 text-gray-400 text-xs truncate">
                                            {formatDate(item.sentAt)}
                                        </td>

                                        <td className="py-4">
                                            <button
                                                onClick={() => {
                                                    setSelectedPhone(item.toNumber);
                                                    setOpenChat(true);
                                                }}
                                                className="text-green-600 hover:text-green-700"
                                            >
                                                <FaComments size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Chat modal */}
            <ChatModal
                open={openChat}
                onClose={() => setOpenChat(false)}
                phone={selectedPhone}
                whatsappBg={whtsappBg}
            />
        </>
    );
}

function SearchBox({
    value = "",
    placeholder = "Search",
    onChange,
}: {
    value?: string;
    placeholder?: string;
    onChange?: (val: string) => void;
}) {
    return (
        <div className="relative w-[70%]">
            <input
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                type="text"
                placeholder={placeholder}
                className="w-full border border-gray-200 rounded-lg h-9 pl-8 text-xs font-medium text-gray-600 outline-none focus:ring-1 focus:ring-blue-400 bg-white placeholder-gray-400"
            />
            <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-300 text-xs pointer-events-none" />
        </div>
    );
}